import { ActionTree, GetterTree, MutationTree } from "vuex";
import ItemService, { PaginatedResponse, pageSize } from '@/services/items';
import { Item } from '../services/model/item'
import { maybeSlowLoad } from '@/utils/slowLoad';

class State {
    totalCount: number
    offset: number
    limit: number
    pages: Array<Page>

    constructor(totalCount: number, offset: number, limit: number, pages: Array<Page>) {
        this.totalCount = totalCount;
        this.offset = offset;
        this.limit = limit;
        this.pages = pages;
    }
};

export class Page {
    items: Item[];
    index: number;

    constructor(items: Item[], index: number = 0) {
        this.items = items;
        this.index = index;
    }
};

const getters = <GetterTree<State, any>>{
    numberOfPages: (state: State) => {
        return numberOfPages(state.totalCount);
    },
    getPage: (state: State) => (pageNr: number) => {
        const indexOfPage = state.pages.findIndex(page => page.index == pageNr - 1);
        // When switching pages, the UI can request a page before it has been loaded.
        // In that case, return an empty list rather than throwing.
        if (indexOfPage === -1) return [];
        return state.pages[indexOfPage].items;
    },
    getItemById: (state: State) => (id: string) => {
        for (const page of state.pages) {
            const item = page.items.find(item => item.id === id);
            if (item) {
                return item;
            }
        }
        return null;
    },
    totalItemCount: (state: State) => {
        return state.totalCount;
    },
    noLoadedItems: (state: State) => {
        return state.pages.length == 0;
    },
    loadedPages: (state: State) => {
        return state.pages.length;
    }
};

const mutations = <MutationTree<State>>{
    addPage: (state: State, paginatedResponse: PaginatedResponse) => {
        const currentPageIndex = getCurrentPageIndex(paginatedResponse.offset);
        const page = new Page(paginatedResponse.items, currentPageIndex);
        state.offset = paginatedResponse.offset;
        state.limit = paginatedResponse.limit;
        state.totalCount = paginatedResponse.totalCount;
        state.pages = state.pages.concat(page);
    },
    updateItem: (state: State, updatedItem: Item) => {
        // Update item in all pages where it exists (not just current page)
        state.pages.forEach((page, pageIdx) => {
            const itemIndex = page.items.findIndex(item => item.id === updatedItem.id);
            if (itemIndex !== -1) {
                // Merge updated properties with existing item to preserve all fields
                const existingItem = page.items[itemIndex];
                console.log('Updating item - existing:', existingItem);
                console.log('Updating item - from API:', updatedItem);
                
                // Merge updated item with existing item
                // Note: This reflects what the API returns, even if it's incorrect
                // If the API swaps title/subtitle or omits fields, that will be visible in the UI
                const mergedItem: Item = {
                    ...existingItem,
                    ...updatedItem,
                    // Explicitly set soldAt from updatedItem (can be undefined when marking unsold)
                    soldAt: updatedItem.soldAt,
                };
                console.log('Updating item - merged:', mergedItem);
                // Create new array with updated item to ensure reactivity
                const newItems = [...page.items];
                newItems[itemIndex] = mergedItem;
                // Replace the entire page object to ensure Vue reactivity
                const updatedPage = {
                    ...page,
                    items: newItems
                };
                // Replace the page in the pages array
                const newPages = [...state.pages];
                newPages[pageIdx] = updatedPage;
                state.pages = newPages;
            }
        });
    },
    addItem: (state: State, item: Item) => {
        // Add item to the first page (page index 0) if it exists
        // New items typically appear at the beginning of the list
        if (state.pages.length > 0 && state.pages[0].index === 0) {
            // Prevent duplicates and keep the page capped to `pageSize`
            const withoutDuplicate = state.pages[0].items.filter(existing => existing.id !== item.id);
            const newItems = [item, ...withoutDuplicate].slice(0, pageSize);
            const updatedPage = {
                ...state.pages[0],
                items: newItems
            };
            // Adding an item shifts offset-based pagination. Any cached pages after page 1
            // are now stale until re-fetched, so we drop them to avoid showing wrong data.
            state.pages = [updatedPage];
            // Increment total count
            state.totalCount += 1;
        }
    }
};

function getCurrentPageIndex(offset: number) {
    return Math.ceil(offset / pageSize);
}

function replaceItem(item: Item, index: number, items: Array<Item>): Array<Item> {
    const copiedItems = [...items];
    copiedItems[index] = item;
    return copiedItems
}

function numberOfPages(totalCount: number) {
    if (totalCount > 0) return Math.ceil(totalCount / pageSize)
    else return 1
}

const actions = <ActionTree<State, any>>{
    loadPage: async (context, page: number) => {
        // Avoid reloading already-cached pages.
        const existingPageIndex = context.state.pages.findIndex(p => p.index === page - 1);
        if (existingPageIndex !== -1) return;

        await maybeSlowLoad();

        const offsetToReadFrom = (page - 1) * pageSize;
        return ItemService.getItems({ offset: offsetToReadFrom, limit: pageSize }).then((paginatedResponse: PaginatedResponse) => {
            if (offsetToReadFrom < paginatedResponse.totalCount) {
                return context.commit('addPage', paginatedResponse);
            }
        });
    },
    updateItem: async (context, item: Item) => {
        return ItemService.updateItem(item).then((updatedItem: Item) => {
            context.commit('updateItem', updatedItem);
            return updatedItem;
        });
    },
    markSold: async (context, item: Item) => {
        return ItemService.markItemSold(item).then((updatedItem: Item) => {
            context.commit('updateItem', updatedItem);
            return updatedItem;
        })
    },
    markUnsold: async (context, item: Item) => {
        return ItemService.markItemUnsold(item).then((updatedItem: Item) => {
            context.commit('updateItem', updatedItem);
            return updatedItem;
        })
    }
};


const ItemsModule = {
    namespaced: true,
    state: new State(0, 0, pageSize, Array<Page>()),
    getters: getters,
    mutations: mutations,
    actions: actions
};

export default ItemsModule;
